/**
 * Index route - Dashboard/Home page
 *
 * Public route that shows the main dashboard.
 * Displays welcome message and dashboard data.
 */
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { useAuth } from "@/providers/auth-provider";
import { useUserSettings } from "@/hooks/use-user-settings";
import { isOnboardingGuardDisabled, shouldForceOnboarding } from "@/lib/onboarding-flags";
import { useDashboard } from "@/hooks";
import {
  DashboardEmptyState,
  InsightsCard,
  PrimaryActionsBar,
  TodayStatusBanner,
  WeekCarousel,
  TrendsMiniCards,
} from "@/components/dashboard";
import { LandingPage } from "@/components/marketing/landing-page";

/**
 * Dashboard page component
 *
 * Shows welcome message and pain history summary.
 * Primary actions are in the top bar (PrimaryActionsBar).
 */
function DashboardPage() {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation("dashboard");
  const { settings, isFetched: settingsFetched } = useUserSettings({ enabled: isAuthenticated });
  const { data, isLoading, isFetching, isRefetching, isError } = useDashboard();
  
  // Unauthenticated state — show marketing landing page
  if (!isAuthenticated) {
    return <LandingPage />;
  }

  const onboardingDisabled = isOnboardingGuardDisabled();
  const onboardingForced = shouldForceOnboarding();
  const needsOnboarding = settingsFetched && !!settings && settings.onboardingCompletedAt === null;

  if (!onboardingDisabled && (onboardingForced || needsOnboarding)) {
    return <Navigate to="/onboarding" />;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <section className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            {t("welcomeBack", { name: user?.name?.split(" ")[0] })}
          </h1>
        </section>

        {/* Loading skeleton */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="h-6 w-32 bg-muted animate-pulse rounded" />
              <div className="h-4 w-48 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-[300px] bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="h-5 w-24 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-[150px] bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !data) {
    return (
      <div className="space-y-6">
        <section className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            {t("welcomeBack", { name: user?.name?.split(" ")[0] })}
          </h1>
        </section>

        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center space-y-4 text-center">
              <p className="text-muted-foreground">
                {t("errorLoad")}
              </p>
              <Button onClick={() => window.location.reload()}>
                {t("reloadPage")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has any data
  // Don't show empty state while still fetching/refetching to avoid flash of empty content
  // This prevents showing empty state when cached stale data indicates no data but a background
  // refetch is in progress that will return actual data
  // Use hasAnyData from API to check if user has ever logged any data (not just this week)
  const hasNoData = !isFetching && !isRefetching && !data.hasAnyData;
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome section */}
      <section className="space-y-1.5 sm:space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {t("welcomeBack", { name: firstName })}
        </h1>
      </section>

      {/* Primary Actions Bar - always visible per spec */}
      <PrimaryActionsBar daysLogged={data.thisWeek.daysLogged} />

      {/* Today's Status Banner - shows only when no check-in exists for today */}
      <TodayStatusBanner
        locationCount={data.today.locationCount}
        hasRecord={data.today.recordId !== null}
        isLoading={isLoading}
        isError={isError}
      />

      {/* Dashboard content */}
      {hasNoData ? (
        // Empty state for new users
        <DashboardEmptyState userName={firstName} />
      ) : (
        // Dashboard with data
        <div className="space-y-3 sm:space-y-4">
          {/* This Week Carousel - replaces Today's Summary + Week Summary */}
          <WeekCarousel
            dashboardData={data}
            isLoading={isLoading}
          />

          <TrendsMiniCards />

          {/* AI-Powered Insights */}
          <InsightsCard />

        </div>
      )}

    </div>
  );
}

export const Route = createFileRoute("/")({
  component: DashboardPage,
});
